import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Form,
  Button,
  Space,
  Input,
  Select,
  Popconfirm,
  message,
  Tag,
  Typography,
  Divider,
  Progress,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  WalletOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import {
  GET_LOAD_PAYFORS,
  HEADERS_EXPORT_BEN,
  POST_UPDATE_PAYFORS,
  POST_ADD_OTHER_WORK_JUDGE,
} from "../../../API/apiUrls";
import MoneyInput from "../../recheckJudgement/componentsReCheckJudgment/MoneyInput";
import ThaiDatePicker from "../../../../hook/ThaiDatePicker";
import THEME from "../contractDetailNew/theme";
import { formatMoney } from "../contractDetailNew/utils";
import { optionsInterest } from "../../../../utils/constant/Interest";
import { optionsMonth } from "../../../../utils/constant/MonthSelect";

const { Text } = Typography;

// ── รายการที่ map กับ field ใน judge ────────────────────────
const FIXED_ITEMS = [
  { PAYFOR: "333", label: "ค่าฤชาศาล", judgeKey: "fee" },
  { PAYFOR: "111", label: "ค่าติดตาม", judgeKey: "trackingFee" },
  { PAYFOR: "222", label: "ค่าทนายความ", judgeKey: "attorneyFees" },
];
const FIXED_PAYFORS = new Set(FIXED_ITEMS.map((i) => i.PAYFOR));

// PAYFOR ที่คำนวณอัตโนมัติ → เพิ่มเองไม่ได้
const AUTO_PAYFORS = new Set(["444", "555"]);

// ดอกเบี้ยส่งเป็น %/ปี (เช่น 15) → ใช้ fill เป็น value
const interestPercentOptions = optionsInterest.map((o) => ({
  value: o.fill,
  label: o.label,
}));

const extraFieldName = (payfor) => `extraCost_${payfor}`;

const ApproveDueModal = ({
  open,
  close,
  judgeData,
  postApporve,
  loadApprovDue,
  setLoadApprovDue,
  onReload,
}) => {
  const [form] = Form.useForm();
  const userName = localStorage.getItem("USERNAME");
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const firstJudge = judgeData?.judge?.[0];
  const arothrData = useMemo(() => judgeData?.arothr ?? [], [judgeData]);
  const contnoArothr = judgeData?.currentNewContno || firstJudge?.newContno;

  // รายการที่เพิ่มใหม่ในหน้านี้ (ยังไม่มีใน arothr)
  const [extraCosts, setExtraCosts] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [selectedForcode, setSelectedForcode] = useState(null);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [availableCodes, setAvailableCodes] = useState([]);
  const [savingArothr, setSavingArothr] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExtraCosts([]);
    setAddingNew(false);
    form.resetFields();
  }, [open, form]);

  // ── คำนวณยอดแบบ live ─────────────────────────────────────
  const watched = Form.useWatch([], form) || {};
  const getNewAmt = (payfor) =>
    parseFloat(watched[extraFieldName(payfor)]) || 0;

  const newTotal = extraCosts.reduce(
    (sum, item) => sum + getNewAmt(item.PAYFOR),
    0,
  );
  const existingAROTHR = Number(firstJudge?.AROTHR || 0);
  const newAROTHR = existingAROTHR + newTotal;

  const totalJudgement =
    Number(firstJudge?.judgement || 0) +
    newAROTHR +
    Number(firstJudge?.costOfUselessness || 0);

  const totalPaid = (judgeData?.chqtran ?? [])
    .filter((r) => r.FLAG !== "C")
    .reduce((sum, item) => sum + parseFloat(item.PAYAMT_N || 0), 0);
  const totalPaidArothr = (judgeData?.chqtran_arothr ?? [])
    .filter((r) => r.FLAG !== "C")
    .reduce((sum, item) => sum + parseFloat(item.PAYAMT || 0), 0);
  const totalPaidAll = totalPaid + totalPaidArothr;
  const remaining = Math.max(0, totalJudgement - totalPaidAll);

  // ── ค่างวด x จำนวนเดือน ต้องไม่เกินยอดคงเหลือ ─────────────
  const calcBalanceAfter = (nopay, upay) =>
    remaining - (Number(nopay) || 0) * (parseFloat(upay) || 0);
  const balanceAfter = calcBalanceAfter(watched.T_NOPAY, watched.TOT_UPAY);
  const showBalanceAfter = !!watched.T_NOPAY && !!watched.TOT_UPAY;

  const paymentProgress =
    totalJudgement > 0 ? (totalPaidAll / totalJudgement) * 100 : 0;

  // ── เรียก API ดึง FORCODE ที่ใช้ได้ ───────────────────────
  const loadForsCodes = useCallback(async () => {
    setLoadingAdd(true);
    try {
      const res = await axios.get(GET_LOAD_PAYFORS, {
        headers: HEADERS_EXPORT_BEN,
      });
      const apiCodes = res.data?.data || [];
      // ค่าฤชาศาล/ค่าติดตาม/ค่าทนาย ที่ยังไม่มี → ให้เลือกเพิ่มได้ด้วย
      const fixedCodes = FIXED_ITEMS.filter(
        (f) => !apiCodes.some((c) => c.FORCODE === f.PAYFOR),
      ).map((f) => ({ FORCODE: f.PAYFOR, FORDESC: f.label }));
      const allCodes = [...fixedCodes, ...apiCodes];
      const usedPayfors = new Set([
        ...AUTO_PAYFORS,
        ...arothrData.map((a) => a.PAYFOR),
        ...extraCosts.map((e) => e.PAYFOR),
      ]);
      const unused = allCodes.filter((code) => !usedPayfors.has(code.FORCODE));
      setAvailableCodes(unused);

      if (unused.length > 0) {
        setSelectedForcode(unused[0].FORCODE);
        setNewLabel(unused[0].FORDESC || "");
      } else {
        setSelectedForcode("0");
        setNewLabel("");
      }
      return true;
    } catch (error) {
      message.error(error?.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้");
      return false;
    } finally {
      setLoadingAdd(false);
    }
  }, [extraCosts, arothrData]);

  // ── POST รายการใหม่ขึ้น API ───────────────────────────────
  const postForsCodes = useCallback(async (forcode, fordesc) => {
    setLoadingAdd(true);
    try {
      const res = await axios.post(
        POST_UPDATE_PAYFORS,
        { FORCODE: forcode, FORDESC: fordesc },
        { headers: HEADERS_EXPORT_BEN },
      );
      return res.status === 201 || res.status === 200;
    } catch (error) {
      message.error(
        error?.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้",
      );
      return false;
    } finally {
      setLoadingAdd(false);
    }
  }, []);

  const handleOpenAdd = useCallback(async () => {
    const ok = await loadForsCodes();
    if (ok) setAddingNew(true);
  }, [loadForsCodes]);

  const handleSelectForcode = useCallback(
    (forcode) => {
      setSelectedForcode(forcode);
      if (forcode === "0") {
        setNewLabel("");
        return;
      }
      const found = availableCodes.find((c) => c.FORCODE === forcode);
      setNewLabel(found?.FORDESC || "");
    },
    [availableCodes],
  );

  const getNextForcode = useCallback(() => {
    const allNums = [
      ...availableCodes.map((c) => parseInt(c.FORCODE, 10)),
      ...extraCosts.map((e) => parseInt(e.PAYFOR, 10)),
      ...arothrData.map((a) => parseInt(a.PAYFOR, 10)),
    ].filter(
      (n) =>
        !isNaN(n) &&
        !AUTO_PAYFORS.has(String(n)) &&
        !FIXED_PAYFORS.has(String(n)),
    );
    const maxNum = allNums.length > 0 ? Math.max(...allNums) : 0;
    return String(maxNum + 1).padStart(3, "0");
  }, [availableCodes, extraCosts, arothrData]);

  const resetAddForm = () => {
    setAddingNew(false);
    setNewLabel("");
    setSelectedForcode(null);
  };

  const handleAddCost = useCallback(async () => {
    const label = newLabel?.trim();
    if (!label) return message.warning("กรุณาระบุชื่อรายการ");
    if (!selectedForcode) return message.warning("กรุณาเลือกรหัสรายการ");

    const isCustom = selectedForcode === "0";
    const resolvedForcode = isCustom ? getNextForcode() : selectedForcode;

    if (
      extraCosts.some((e) => e.PAYFOR === resolvedForcode) ||
      arothrData.some((a) => a.PAYFOR === resolvedForcode)
    ) {
      return message.warning(`รหัส ${resolvedForcode} ถูกเพิ่มไปแล้ว`);
    }

    if (isCustom) {
      const lower = label.toLowerCase();
      const isDuplicateLabel =
        extraCosts.some((e) => e.label.toLowerCase() === lower) ||
        availableCodes.some((c) => c.FORDESC?.toLowerCase() === lower);
      if (isDuplicateLabel) {
        return message.warning(`ชื่อรายการ "${label}" มีอยู่แล้ว`);
      }
      const ok = await postForsCodes(resolvedForcode, label);
      if (!ok) return;
    }

    const fieldName = extraFieldName(resolvedForcode);
    setExtraCosts((prev) => [
      ...prev,
      { PAYFOR: resolvedForcode, label, fieldName },
    ]);
    form.setFieldsValue({ [fieldName]: null });
    resetAddForm();
    message.success(`เพิ่ม "${label}" (${resolvedForcode}) สำเร็จ`);
  }, [
    newLabel,
    selectedForcode,
    getNextForcode,
    extraCosts,
    availableCodes,
    postForsCodes,
    form,
    arothrData,
  ]);

  const handleRemoveCost = useCallback(
    (payfor) => {
      setExtraCosts((prev) => prev.filter((item) => item.PAYFOR !== payfor));
      form.setFieldsValue({ [extraFieldName(payfor)]: null });
    },
    [form],
  );

  // ── บันทึกลูกหนี้อื่นๆ ที่เพิ่มใหม่ ─────────────────────────
  const postAddOtherWork = useCallback(async (payload) => {
    console.log("POST_ADD_OTHER_WORK_JUDGE:", payload);

    try {
      const res = await axios.post(POST_ADD_OTHER_WORK_JUDGE, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200 || res.status === 201) {
        message.success("บันทึกข้อมูลสำเร็จ");
        return res.status;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(`ไม่สามารถส่งข้อมูลได้: ${serverMsg}`);
      }
      throw error;
    }
  }, []);

  // ── บันทึกลูกหนี้อื่นๆ (แยกจากอนุมัติ) ───────────────────
  const handleSaveArothr = async () => {
    if (extraCosts.length === 0) {
      return message.warning("กรุณาเพิ่มลูกหนี้อื่นๆ ก่อน");
    }
    const values = form.getFieldsValue();

    const getAmt = (payfor) => parseFloat(values[extraFieldName(payfor)]) || 0;
    const addedRows = extraCosts.filter((item) => getAmt(item.PAYFOR) > 0);
    if (addedRows.length === 0) {
      return message.warning("กรุณากรอกจำนวนเงินลูกหนี้อื่นๆ");
    }

    const payload = addedRows.map((item) => ({
      CONTNO: contnoArothr,
      idJudge: firstJudge?.idJudge,
      PAYFOR: item.PAYFOR,
      PAYAMT: getAmt(item.PAYFOR),
      USERID: userName,
    }));

    setSavingArothr(true);
    try {
      const status = await postAddOtherWork(payload);
      if (!status) return;

      // ล้างรายการใหม่ แล้วโหลดข้อมูลใหม่ → รายการที่บันทึกแล้วจะขึ้นแบบ disabled
      form.setFieldsValue(
        Object.fromEntries(
          extraCosts.map((item) => [extraFieldName(item.PAYFOR), null]),
        ),
      );
      setExtraCosts([]);
      resetAddForm();
      await onReload?.();
    } catch (error) {
      console.error("Save arothr error:", error);
    } finally {
      setSavingArothr(false);
    }
  };

  // ── อนุมัติขอผ่อนชำระ ─────────────────────────────────────
  const handleApprove = async () => {
    if (extraCosts.length > 0) {
      return message.warning(
        "มีลูกหนี้อื่นๆ ที่ยังไม่ได้บันทึก กรุณากดบันทึกหรือลบรายการก่อน",
      );
    }
    let values;
    try {
      values = await form.validateFields([
        "dueDate",
        "T_NOPAY",
        "TOT_UPAY",
        "annualInterestRate",
      ]);
    } catch {
      return;
    }

    const result = await postApporve({
      idJudge: firstJudge?.idJudge,
      FDATE: dayjs(values.dueDate).format("YYYY-MM-DD"),
      T_NOPAY: values.T_NOPAY, // จำนวนงวด
      TOT_UPAY: Math.round(parseFloat(values.TOT_UPAY) || 0), // ค่างวด จำนวนเต็ม
      annualInterestRate: values.annualInterestRate, // %/ปี , 0 = ไม่คิดดอก
    });
    setLoadApprovDue(false);
    if (!result) return;

    if (result.status === "success") {
      message.success(result.message, 3);
    } else {
      message.error(result.message, 3);
    }
    if (!result.error) {
      onReload?.();
      close();
    }
  };

  const moneyText = (value, color) => (
    <Text strong style={{ color, fontSize: 17 }}>
      {formatMoney(value)}{" "}
      <span style={{ color: "#aaa", fontSize: 13, fontWeight: 400 }}>บาท</span>
    </Text>
  );

  const statBox = (label, value, color) => (
    <div
      style={{
        background: "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: "10px 12px",
        textAlign: "center",
      }}
    >
      <Text
        type="secondary"
        style={{ fontSize: 13, display: "block", marginBottom: 4 }}
      >
        {label}
      </Text>
      {moneyText(value, color)}
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: THEME.success }} />
          <span>อนุมัติขอผ่อนชำระ</span>
        </Space>
      }
      open={open}
      onOk={handleApprove}
      onCancel={close}
      okText="อนุมัติ"
      cancelText="ยกเลิก"
      confirmLoading={loadApprovDue}
      centered
      width={800}
      okButtonProps={{
        style: { background: THEME.success, borderColor: THEME.success },
      }}
      styles={{
        header: { paddingBottom: 12 },
        body: { paddingTop: 4, maxHeight: "75vh", overflowY: "auto" },
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #f6ffed 0%, #fff 100%)",
          border: "1px solid #b7eb8f",
          borderRadius: THEME.borderRadius,
          padding: "18px 20px",
          marginBottom: 20,
        }}
      >
        <Space align="center" style={{ marginBottom: 14 }}>
          <WalletOutlined style={{ color: THEME.success, fontSize: 15 }} />
          <Text strong style={{ fontSize: 15 }}>
            ความคืบหน้าการชำระ
          </Text>
          <Tag
            color={paymentProgress >= 100 ? "success" : "processing"}
            style={{ borderRadius: 10, marginInlineEnd: 0 }}
          >
            {Number(paymentProgress.toFixed(1))}%
          </Tag>
        </Space>
        <Progress
          percent={Number(paymentProgress.toFixed(1))}
          strokeColor={{ "0%": THEME.success, "100%": "#52c41a" }}
          trailColor="#e6f7e6"
          size={{ width: "100%", height: 10 }}
          style={{ marginBottom: 16 }}
        />
        <Row gutter={12}>
          <Col span={8}>{statBox("ยอดตามคำพิพากษา", totalJudgement)}</Col>
          <Col span={8}>{statBox("ชำระแล้ว", totalPaidAll, THEME.success)}</Col>
          <Col span={8}>{statBox("คงเหลือ", remaining, THEME.danger)}</Col>
        </Row>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fffbe6",
            border: `1px solid ${THEME.warning}`,
            borderRadius: 8,
            padding: "10px 14px",
            marginTop: 12,
          }}
        >
          <Space size={6}>
            <ExclamationCircleOutlined
              style={{ color: THEME.warning, fontSize: 15 }}
            />
            <Text strong style={{ fontSize: 13 }}>
              ยอดขอผ่อน 10%
            </Text>
          </Space>
          {moneyText(totalJudgement / 10, THEME.warning)}
        </div>
      </div>

      <Form form={form} layout="horizontal" labelCol={{ span: 9 }}>
        <Divider orientation="left">💵 ลูกหนี้อื่น</Divider>

        {/* ── รายการที่มีอยู่แล้ว → แก้ไขไม่ได้ ───────────── */}
        {arothrData.map((row) => (
          <Form.Item key={row.PAYFOR} label={row.FORDESC || row.PAYFOR}>
            <MoneyInput value={parseFloat(row.PAYAMT) || 0} disabled />
          </Form.Item>
        ))}

        {/* ── รายการที่เพิ่มใหม่ ─────────────────────────────── */}
        {extraCosts.map((item) => (
          <Form.Item
            key={item.PAYFOR}
            label={
              <Space size={4}>
                {item.label}
                <Tag color="green">ใหม่</Tag>
              </Space>
            }
          >
            <Space>
              <Form.Item name={item.fieldName} noStyle>
                <MoneyInput placeholder="กรอกจำนวนเงิน" />
              </Form.Item>
              <Popconfirm
                title="ลบรายการนี้?"
                description={`"${item.label}" จะถูกลบออก`}
                onConfirm={() => handleRemoveCost(item.PAYFOR)}
                okText="ลบ"
                cancelText="ยกเลิก"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </Space>
          </Form.Item>
        ))}

        {!addingNew ? (
          <Form.Item label=" " colon={false}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleOpenAdd}
              loading={loadingAdd}
              style={{ borderColor: "#1890ff", color: "#1890ff" }}
            >
              เพิ่มลูกหนี้อื่นๆ
            </Button>
          </Form.Item>
        ) : (
          <div
            style={{
              background: "#fafafa",
              border: "1px dashed #d9d9d9",
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
            }}
          >
            <Text strong style={{ display: "block", marginBottom: 12 }}>
              เพิ่มรายการใหม่
            </Text>
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                >
                  เลือกรหัสรายการ
                </Text>
                <Select
                  value={selectedForcode}
                  onChange={handleSelectForcode}
                  style={{ width: "100%" }}
                  placeholder="เลือกรหัส"
                  options={[
                    ...availableCodes.map((c) => ({
                      value: c.FORCODE,
                      label: `${c.FORCODE} — ${c.FORDESC}`,
                    })),
                    { value: "0", label: "➕ เพิ่มรายการใหม่ (กำหนดเอง)" },
                  ]}
                />
              </div>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                >
                  ชื่อรายการ
                  {selectedForcode === "0" && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      รหัสใหม่: {getNextForcode()}
                    </Tag>
                  )}
                </Text>
                <Input
                  placeholder='เช่น "ค่าเสียหาย" "ค่าปรับ"'
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onPressEnter={handleAddCost}
                  disabled={selectedForcode !== "0"}
                />
              </div>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCost}
                  loading={loadingAdd}
                >
                  เพิ่ม
                </Button>
                <Button onClick={resetAddForm}>ยกเลิก</Button>
              </Space>
            </Space>
          </div>
        )}

        <Form.Item label="รวมลูกหนี้อื่น">
          <Space size={12}>
            <Text strong>{formatMoney(newAROTHR)} บาท</Text>
            {extraCosts.length > 0 && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveArothr}
                loading={savingArothr}
              >
                บันทึกลูกหนี้อื่นๆ
              </Button>
            )}
          </Space>
        </Form.Item>

        <Divider style={{ margin: "16px 0" }} />

        <Form.Item
          name="dueDate"
          label={
            <Space size={6}>
              <CalendarOutlined style={{ color: THEME.primary }} />
              <Text strong>วันที่เริ่มดิว</Text>
            </Space>
          }
          rules={[{ required: true, message: "กรุณาเลือกวันที่" }]}
        >
          <ThaiDatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
        </Form.Item>

        <Form.Item
          name="T_NOPAY"
          label={<Text strong>จำนวนงวด</Text>}
          dependencies={["TOT_UPAY"]}
          rules={[
            { required: true, message: "กรุณากรอกจำนวนงวด" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const upay = getFieldValue("TOT_UPAY");
                if (!value || !upay || calcBalanceAfter(value, upay) >= 0) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "ค่างวด x จำนวนเดือน เกินยอดคงเหลือ กรุณาลดจำนวนเดือน",
                  ),
                );
              },
            }),
          ]}
        >
          <Select
            showSearch
            size="large"
            style={{ width: "100%" }}
            popupMatchSelectWidth={false}
            optionFilterProp="label"
            options={optionsMonth}
            placeholder="เลือกจำนวนเดือน"
            suffix="เดือน"
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="TOT_UPAY"
          label={<Text strong>ค่างวด</Text>}
          rules={[{ required: true, message: "กรุณากรอกค่างวด" }]}
        >
          <MoneyInput placeholder="กรอกค่างวด" />
        </Form.Item>

        {showBalanceAfter && (
          <Form.Item label="คงเหลือหลังผ่อนให้ไปรวมกับงวดสุดท้าย">
            <Text strong type={balanceAfter < 0 ? "danger" : "success"}>
              {formatMoney(balanceAfter)} บาท
            </Text>
          </Form.Item>
        )}

        <Form.Item
          name="annualInterestRate"
          label={<Text strong>คิดดอกเบี้ย</Text>}
          rules={[{ required: true, message: "กรุณาเลือกอัตราดอกเบี้ย" }]}
        >
          <Select
            showSearch
            options={interestPercentOptions}
            optionFilterProp="label"
            size="large"
            style={{ width: "100%" }}
            placeholder="เลือกอัตราดอกเบี้ย"
            popupMatchSelectWidth={false}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ApproveDueModal;
